import { keepPreviousData, type QueryClient, queryOptions, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { ItemsApi } from '@/common/api/items';
import type { ItemFiltersType } from '@/common/api/types/itemFilters';
import type { ItemTreeSet } from '@/common/api/types/items';
import { useFlag } from '@/common/hooks/feature-flags/useFlag';
import { fetchReportingStoreItemsTreeSet } from '@/common/queries/treeSet';
import { commonQueryKeys } from '@/common/queryKeys/common';
import { itemsTreeSetQueryKey } from '@/features/items/queryKeys/itemsTreeSetKey';

export const invalidateItemsDataSet = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: commonQueryKeys.itemsDataSet.all() });
};

export const itemsDataSetQueryOptions = ({
  order,
  filter,
  enabled,
  keepPrevious = false,
}: {
  order?: string;
  filter?: ItemFiltersType;
  enabled?: boolean;
  keepPrevious?: boolean;
}) => {
  return queryOptions({
    queryKey: [...commonQueryKeys.itemsDataSet.byFilter(filter), order],
    queryFn: () => ItemsApi.getItemsDataSet({ filter, order }),
    enabled,
    placeholderData: keepPrevious ? keepPreviousData : undefined,
  });
};

export const useItemsDataSetQuery = ({
  filter,
  enabled,
  keepPrevious = false,
}: {
  filter?: ItemFiltersType;
  enabled?: boolean;
  keepPrevious?: boolean;
} = {}) => useQuery(itemsDataSetQueryOptions({ filter, enabled, keepPrevious }));

export const getItemsTreeSetQueryOptions = ({
  filter,
  order,
  includeDescendants = false,
  useReportingStore = false,
}: {
  filter?: ItemFiltersType;
  order?: string;
  includeDescendants?: boolean;
  useReportingStore?: boolean;
} = {}) =>
  queryOptions({
    queryKey: itemsTreeSetQueryKey({ filter, order, includeDescendants, useReportingStore }),
    queryFn: () =>
      useReportingStore
        ? fetchReportingStoreItemsTreeSet({ filter, order, includeDescendants })
        : ItemsApi.getItemsTreeSet({ filter, order, includeDescendants }),
  });

export const useItemsTreeSetQuery = <TData = ItemTreeSet>({
  filter,
  order,
  enabled,
  keepPrevious = false,
  select,
  staleTime,
  refetchOnMount,
  includeDescendants,
}: {
  filter?: ItemFiltersType;
  order?: string;
  enabled?: boolean;
  keepPrevious?: boolean;
  select?: (data: ItemTreeSet) => TData;
  staleTime?: number;
  refetchOnMount?: boolean;
  includeDescendants?: boolean;
} = {}) => {
  const useReportingStore = useFlag('reportingServiceFilter');
  const options = useMemo(
    () => getItemsTreeSetQueryOptions({ filter, order, includeDescendants, useReportingStore }),
    [filter, order, includeDescendants, useReportingStore],
  );

  const query = useQuery({
    ...options,
    enabled,
    placeholderData: keepPrevious ? keepPreviousData : undefined,
    select,
    staleTime,
    refetchOnMount,
  });

  // So callers (e.g. the Update banner) key off this resolved value instead of re-deriving it.
  return { ...query, queryKey: options.queryKey };
};
